interface ProviderProfileDraft {
  providerType: 'individual' | 'sole-trader-trading' | 'company' | 'organisation';
  businessName: string;
  supportWorkerName: string;
  abn: string;
  invoiceEmail: string;
  phone: string;
  address: string;
  serviceTypes: string;
  defaultHourlyRate: string;
  defaultKmRate: string;
  gstRegistered: 'yes' | 'no' | 'unknown';
  paymentTerms: string;
  invoiceFooter: string;
  paymentAccountName: string;
  paymentBSB: string;
  paymentAccountNumber: string;
  paymentReferencePreference: 'invoice-number' | 'participant-name' | 'custom';
  paymentReferenceCustom: string;
  paymentNotes: string;
  paymentDetailsPlaceholder: string;
}

const PROVIDER_STORAGE_KEY = 'hub-provider-draft';
const PRIVACY_TEXT = 'To request deletion or modification of your submitted profile information, email accounts@the-hub-ecosystems.com. Where possible, send the request from the same email address used when submitting the information. If that email was entered incorrectly, is unavailable, or may be slightly different, please explain this in your request. Additional verification may be required before records are changed or deleted.';

let providerDraft: ProviderProfileDraft = defaultProviderDraft();

function defaultProviderDraft(): ProviderProfileDraft {
  return {
    providerType: 'individual',
    businessName: '',
    supportWorkerName: '',
    abn: '',
    invoiceEmail: '',
    phone: '',
    address: '',
    serviceTypes: '',
    defaultHourlyRate: '',
    defaultKmRate: '',
    gstRegistered: 'unknown',
    paymentTerms: '',
    invoiceFooter: '',
    paymentAccountName: '',
    paymentBSB: '',
    paymentAccountNumber: '',
    paymentReferencePreference: 'invoice-number',
    paymentReferenceCustom: '',
    paymentNotes: '',
    paymentDetailsPlaceholder: '',
  };
}

function demoProviderDraft(): ProviderProfileDraft {
  return {
    providerType: 'sole-trader-trading',
    businessName: 'Demo Support Services',
    supportWorkerName: 'Jamie Demo Worker',
    abn: '12 345 678 901',
    invoiceEmail: 'accounts@example.com',
    phone: '0400 333 444',
    address: '20 Demo Avenue, Sydney NSW 2000',
    serviceTypes: 'Community access, daily living, transport',
    defaultHourlyRate: '67.56',
    defaultKmRate: '0.97',
    gstRegistered: 'no',
    paymentTerms: 'Due within 7 days of invoice date.',
    invoiceFooter: 'Generated as a reviewed draft only.',
    paymentAccountName: 'Demo Support Services',
    paymentBSB: '000-000',
    paymentAccountNumber: '0000 0000',
    paymentReferencePreference: 'invoice-number',
    paymentReferenceCustom: '',
    paymentNotes: 'Demo payment details only. Do not use for payment.',
    paymentDetailsPlaceholder: 'Payment details withheld in test profile.',
  };
}

function injectStyles(): void {
  if (document.getElementById('provider-profile-builder-styles')) return;
  const style = document.createElement('style');
  style.id = 'provider-profile-builder-styles';
  style.textContent = `
body.sp-public-body {
  display: block !important;
  min-height: 100dvh;
  height: auto !important;
  overflow-y: auto !important;
  background: var(--bg) !important;
}
.sp-public-root {
  display: block !important;
  width: 100% !important;
  min-height: 100dvh !important;
  height: auto !important;
  overflow: visible !important;
  flex: none !important;
  background: var(--bg);
}
.prb-page {
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: var(--bg);
  color: var(--fg);
  font-family: var(--font-sans);
  padding: 24px;
}
.prb-shell { width: 100%; max-width: 1120px; margin: 0 auto; }
.prb-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}
.prb-brand { color: var(--fg-muted); font-size: 12px; font-weight: 700; text-transform: uppercase; }
.prb-header h1 { margin: 6px 0 6px; color: var(--fg); font-size: 24px; line-height: 1.2; font-weight: 800; }
.prb-header p { margin: 0; color: var(--fg-muted); font-size: 14px; line-height: 1.45; }
.prb-badges { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.prb-badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 9px;
  border-radius: 4px;
  border: 1px solid var(--warning);
  background: var(--bg-secondary);
  color: var(--warning);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.prb-warning-stack { display: grid; gap: 8px; margin-bottom: 16px; }
.prb-warning {
  padding: 12px 14px;
  border: 1px solid var(--warning);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--fg);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 700;
}
.prb-card {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-secondary);
  padding: 14px;
  margin-bottom: 14px;
}
.prb-section-title { margin: 0 0 10px; color: var(--fg); font-size: 16px; line-height: 1.25; font-weight: 800; }
.prb-section-help { margin: -4px 0 12px; color: var(--fg-muted); font-size: 12px; line-height: 1.45; }
.prb-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.prb-radio-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
.prb-radio {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  min-height: 54px;
  box-sizing: border-box;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  padding: 9px 10px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}
.prb-radio input { flex: 0 0 auto; margin-top: 2px; accent-color: var(--accent); }
.prb-field { min-width: 0; }
.prb-field-wide { grid-column: 1 / -1; }
.prb-label { display: block; margin-bottom: 5px; color: var(--fg-muted); font-size: 12px; font-weight: 700; }
.prb-input, .prb-select, .prb-textarea {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
}
.prb-textarea { min-height: 94px; resize: vertical; }
.prb-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.prb-button {
  min-height: 38px;
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: var(--accent);
  color: var(--bg);
  padding: 8px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.prb-button-secondary { border-color: var(--border); background: var(--bg); color: var(--fg); }
.prb-notice {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  font-size: 13px;
  line-height: 1.55;
}
@media (max-width: 768px) {
  .prb-page { padding: 16px; }
  .prb-topbar { flex-direction: column; }
  .prb-badges { justify-content: flex-start; }
  .prb-fields, .prb-radio-grid { grid-template-columns: 1fr; }
  .prb-actions { justify-content: stretch; }
  .prb-button { width: 100%; }
}
`;
  document.head.appendChild(style);
}

function shell(): HTMLElement {
  document.title = 'Provider Profile Builder - Hub People';
  injectStyles();
  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';
  for (const id of ['sidebar-scrim', 'modal-overlay', 'toast']) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  const page = document.createElement('div');
  page.className = 'prb-page';
  root.appendChild(page);
  return page;
}

function esc(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function readDraft(): ProviderProfileDraft {
  try {
    const raw = localStorage.getItem(PROVIDER_STORAGE_KEY);
    if (!raw) return defaultProviderDraft();
    const parsed = JSON.parse(raw) as Partial<ProviderProfileDraft>;
    return { ...defaultProviderDraft(), ...parsed };
  } catch {
    return defaultProviderDraft();
  }
}

function saveDraft(): void {
  localStorage.setItem(PROVIDER_STORAGE_KEY, JSON.stringify(providerDraft));
}

function inputField(field: keyof ProviderProfileDraft, label: string, wide = false): string {
  return `
    <label class="prb-field${wide ? ' prb-field-wide' : ''}">
      <span class="prb-label">${esc(label)}</span>
      <input class="prb-input" data-field="${esc(field)}" type="text" value="${esc(String(providerDraft[field]))}">
    </label>
  `;
}

function providerTypeField(): string {
  const options: Array<{ value: ProviderProfileDraft['providerType']; label: string }> = [
    { value: 'individual', label: 'Individual / sole trader' },
    { value: 'sole-trader-trading', label: 'Sole trader with trading name' },
    { value: 'company', label: 'Company / Pty Ltd' },
    { value: 'organisation', label: 'Organisation / provider business' },
  ];
  return `
    <div class="prb-radio-grid" role="radiogroup" aria-label="Provider type">
      ${options.map(option => `
        <label class="prb-radio">
          <input type="radio" name="providerType" data-field="providerType" value="${esc(option.value)}"${providerDraft.providerType === option.value ? ' checked' : ''}>
          <span>${esc(option.label)}</span>
        </label>
      `).join('')}
    </div>
  `;
}

function referencePreferenceField(): string {
  const options: Array<{ value: ProviderProfileDraft['paymentReferencePreference']; label: string }> = [
    { value: 'invoice-number', label: 'Invoice number' },
    { value: 'participant-name', label: 'Participant name' },
    { value: 'custom', label: 'Custom' },
  ];
  return `
    <label class="prb-field">
      <span class="prb-label">Payment reference preference</span>
      <select class="prb-select" data-field="paymentReferencePreference">
        ${options.map(option => `<option value="${esc(option.value)}"${providerDraft.paymentReferencePreference === option.value ? ' selected' : ''}>${esc(option.label)}</option>`).join('')}
      </select>
    </label>
  `;
}

function textareaField(field: keyof ProviderProfileDraft, label: string): string {
  return `
    <label class="prb-field prb-field-wide">
      <span class="prb-label">${esc(label)}</span>
      <textarea class="prb-textarea" data-field="${esc(field)}">${esc(String(providerDraft[field]))}</textarea>
    </label>
  `;
}

function gstField(): string {
  const options: ProviderProfileDraft['gstRegistered'][] = ['unknown', 'yes', 'no'];
  return `
    <label class="prb-field">
      <span class="prb-label">GST registered</span>
      <select class="prb-select" data-field="gstRegistered">
        ${options.map(option => `<option value="${esc(option)}"${providerDraft.gstRegistered === option ? ' selected' : ''}>${esc(option)}</option>`).join('')}
      </select>
    </label>
  `;
}

function renderContent(page: HTMLElement): void {
  const isIndividual = providerDraft.providerType === 'individual';
  const isTrading = providerDraft.providerType === 'sole-trader-trading';
  const isBusinessEntity = providerDraft.providerType === 'company' || providerDraft.providerType === 'organisation';
  page.innerHTML = `
    <main class="prb-shell">
      <div class="prb-topbar">
        <header class="prb-header">
          <div class="prb-brand">the hub ecosystems</div>
          <h1>Provider Profile Builder</h1>
          <p>Internal test provider draft for NDIS-aware invoicing.</p>
        </header>
        <div class="prb-badges">
          <span class="prb-badge">Auth-gated</span>
          <span class="prb-badge">Local only</span>
          <span class="prb-badge">Test</span>
        </div>
      </div>
      <div class="prb-warning-stack">
        <div class="prb-warning">No real banking details saved.</div>
        <div class="prb-warning">No real provider profile created.</div>
        <div class="prb-warning">No invoice sent. Review required.</div>
      </div>
      <section class="prb-card" aria-label="Provider type">
        <h2 class="prb-section-title">Provider Type</h2>
        <div class="prb-section-help">Controls how the billing identity appears on invoices.</div>
        ${providerTypeField()}
        <div class="prb-fields">
          ${isBusinessEntity ? inputField('businessName', 'Business/company name') : ''}
          ${isTrading ? inputField('supportWorkerName', 'Person/legal name') : ''}
          ${isTrading ? inputField('businessName', 'Trading/business name') : ''}
          ${isIndividual ? inputField('supportWorkerName', 'Person/legal name') : ''}
          ${isBusinessEntity ? inputField('supportWorkerName', 'Contact person') : ''}
          ${inputField('abn', 'ABN')}
          ${inputField('invoiceEmail', 'Invoice email')}
          ${inputField('phone', 'Phone (optional)')}
          ${inputField('address', 'Address (optional)')}
        </div>
      </section>
      <section class="prb-card" aria-label="Service and invoice defaults">
        <h2 class="prb-section-title">Service &amp; Invoice Defaults</h2>
        <div class="prb-fields">
          ${inputField('serviceTypes', 'Service types', true)}
          ${inputField('defaultHourlyRate', 'Default hourly rate placeholder')}
          ${inputField('defaultKmRate', 'Default km rate placeholder')}
          ${gstField()}
          ${textareaField('paymentTerms', 'Payment terms text')}
          ${textareaField('invoiceFooter', 'Invoice footer/details')}
        </div>
      </section>
      <section class="prb-card" aria-label="Billing and payment">
        <h2 class="prb-section-title">Billing / Payment</h2>
        <div class="prb-section-help">Structured payment details are local test draft fields only.</div>
        <div class="prb-fields">
          ${inputField('paymentAccountName', 'Payment account name')}
          ${inputField('paymentBSB', 'BSB')}
          ${inputField('paymentAccountNumber', 'Account number')}
          ${referencePreferenceField()}
          ${providerDraft.paymentReferencePreference === 'custom' ? inputField('paymentReferenceCustom', 'Custom reference') : ''}
          ${textareaField('paymentNotes', 'Payment notes')}
          ${textareaField('paymentDetailsPlaceholder', 'Advanced fallback payment details')}
        </div>
        <div class="prb-actions">
          <button class="prb-button prb-button-secondary" id="prb-load-demo" type="button">Load demo</button>
          <button class="prb-button" id="prb-save" type="button">Save local draft</button>
        </div>
        <div class="prb-notice">${esc(PRIVACY_TEXT)}</div>
      </section>
    </main>
  `;
  wireEvents(page);
}

function syncField(field: keyof ProviderProfileDraft, value: string): void {
  if (field === 'providerType') {
    const allowed: ProviderProfileDraft['providerType'][] = ['individual', 'sole-trader-trading', 'company', 'organisation'];
    providerDraft.providerType = allowed.includes(value as ProviderProfileDraft['providerType'])
      ? value as ProviderProfileDraft['providerType']
      : 'individual';
    return;
  }
  if (field === 'gstRegistered') {
    const allowed: ProviderProfileDraft['gstRegistered'][] = ['yes', 'no', 'unknown'];
    providerDraft.gstRegistered = allowed.includes(value as ProviderProfileDraft['gstRegistered'])
      ? value as ProviderProfileDraft['gstRegistered']
      : 'unknown';
    return;
  }
  if (field === 'paymentReferencePreference') {
    const allowed: ProviderProfileDraft['paymentReferencePreference'][] = ['invoice-number', 'participant-name', 'custom'];
    providerDraft.paymentReferencePreference = allowed.includes(value as ProviderProfileDraft['paymentReferencePreference'])
      ? value as ProviderProfileDraft['paymentReferencePreference']
      : 'invoice-number';
    return;
  }
  providerDraft = { ...providerDraft, [field]: value };
}

function wireEvents(page: HTMLElement): void {
  page.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-field]').forEach(input => {
    input.addEventListener('input', () => {
      const field = input.dataset['field'] as keyof ProviderProfileDraft | undefined;
      if (!field) return;
      syncField(field, input.value);
      saveDraft();
    });
    input.addEventListener('change', () => {
      const field = input.dataset['field'] as keyof ProviderProfileDraft | undefined;
      if (!field) return;
      syncField(field, input.value);
      saveDraft();
      if (field === 'providerType' || field === 'paymentReferencePreference') renderContent(page);
    });
  });
  page.querySelector<HTMLButtonElement>('#prb-load-demo')?.addEventListener('click', () => {
    providerDraft = demoProviderDraft();
    saveDraft();
    renderContent(page);
  });
  page.querySelector<HTMLButtonElement>('#prb-save')?.addEventListener('click', () => {
    saveDraft();
    renderContent(page);
  });
}

export function isProviderProfileBuilderPath(): boolean {
  return window.location.pathname.includes('provider-profile-builder');
}

export function renderProviderProfileBuilder(): void {
  providerDraft = readDraft();
  renderContent(shell());
}
